<template>
  <div
    v-if="localThumbnails.length"
    ref="imagesContainer"
    class="single-obituary-comment-images"
    :class="{ animate: setAnimationClass }"
  >
    <ix-picture v-for="thumbnail in localThumbnails" :key="thumbnail">
      <ix-source
        :src="thumbnail"
        media="(max-width:479px)"
        :imgix-params="{
          w: 418,
          ...imgixParams,
        }"
      />
      <ix-source
        :src="thumbnail"
        media="(max-width:767px)"
        :imgix-params="{
          w: 705,
          ...imgixParams,
        }"
      />
      <ix-source
        :src="thumbnail"
        media="(min-width:1200px)"
        :imgix-params="{
          w: 523,
          ...imgixParams,
        }"
      />
      <ix-source
        :src="thumbnail"
        media="(min-width:992px)"
        :imgix-params="{
          w: 418,
          ...imgixParams,
        }"
      />
      <ix-source
        :src="thumbnail"
        media="(min-width:768px)"
        :imgix-params="{
          w: 298,
          ...imgixParams,
        }"
      />
      <ix-img
        :src="thumbnail"
        width="300"
        height="200"
        loading="lazy"
        :imgix-params="{
          w: 300,
          ...imgixParams,
        }"
        :alt="altText"
      />
    </ix-picture>
  </div>
</template>

<script>
  import throttle from "lodash-es/throttle";
  import { toRef } from "vue";
  import { mapGetters } from "vuex";
  import { useSingleCommentMedia } from "../composables/useSingleCommentMedia";

  export default {
    name: "SingleCommentImages",

    setup(props) {
      const commentRef = toRef(props, "comment");
      const {
        images,
        videos,
        firstVideo,
        firstImage,
        thumbnail,
        hasMultipleMedia,
        hasMedia,
        thumbnails,
      } = useSingleCommentMedia(commentRef);
      return {
        images,
        videos,
        firstVideo,
        firstImage,
        thumbnail,
        hasMultipleMedia,
        hasMedia,
        thumbnails,
      };
    },

    props: {
      comment: {
        type: Object,
        required: true,
      },
      altText: {
        type: String,
        default: "",
      },
    },

    data() {
      return {
        imgIxDomain: `https://${lavendlaObituary.imgIxDomain}/`,
        imgixParams: {
          fit: "crop",
          crop: "faces,edges",
          ar: "3:2",
        },
        localThumbnails: [],
        thumbnailsLoaded: false,
        isInView: false,
        isAnimating: false,
        setAnimationClass: false,
        animateImagesInterval: null,
        throttledScrollHandler: null,
      };
    },

    computed: {
      ...mapGetters("global", ["isMobile"]),

      imageHeight() {
        return this.isMobile ? 199 : 160;
      },
    },

    watch: {
      thumbnail: {
        handler(newThumb) {
          this.getInitialImage(newThumb);
        },
        immediate: true,
      },
    },

    mounted() {
      this.throttledScrollHandler = throttle(this.handleScroll, 100);
      window.addEventListener("scroll", this.throttledScrollHandler, {
        passive: true,
      });
    },

    beforeUnmount() {
      if (this.throttledScrollHandler) {
        window.removeEventListener("scroll", this.throttledScrollHandler);
        this.throttledScrollHandler.cancel?.();
        this.throttledScrollHandler = null;
      }
      this.clearAnimateImagesInterval();
    },

    methods: {
      getInitialImage(newThumb) {
        if (!newThumb) {
          this.localThumbnails = [];
          return;
        }
        this.localThumbnails = [newThumb];
      },

      isInViewport(el) {
        const rect = el.getBoundingClientRect();
        this.isInView =
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <=
            (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <=
            (window.innerWidth || document.documentElement.clientWidth);

        return this.isInView;
      },

      // Kind of lazyloading the rest of the images when comment is in viewport
      maybyLoadAllImages() {
        if (
          this.thumbnails.length === this.localThumbnails.length ||
          this.thumbnailsLoaded
        ) {
          return;
        }

        // Make sure the visable image is the same image as the image
        // previously loaded. It's only the last image in the DOM that
        // is visible.
        this.shiftImages();
        this.thumbnailsLoaded = true;
      },

      handleScroll() {
        const imagesContainer = this.$refs.imagesContainer;
        if (!imagesContainer) {
          return;
        }

        this.isInView = this.isInViewport(imagesContainer);

        if (!this.isInView) {
          this.isMobile && this.animateImagesStop();
          return;
        }

        this.maybyLoadAllImages();
        this.isMobile && this.animateImages();
      },

      shiftImages() {
        const firstImage = this.thumbnails.shift();
        this.thumbnails.push(firstImage);

        this.localThumbnails = [...this.thumbnails];
      },

      unShiftImages() {
        const lastImage = this.thumbnails.pop();
        this.thumbnails.unshift(lastImage);

        this.localThumbnails = [...this.thumbnails];
      },

      animateImages() {
        if (
          this.isAnimating ||
          !this.thumbnailsLoaded ||
          this.localThumbnails.length < 2
        ) {
          return;
        }

        // Clear interval if it exists
        this.clearAnimateImagesInterval();

        // Start animation
        this.isAnimating = true;
        this.setAnimationClass = true;
        this.shiftImages();

        // Continue animation and set interval enabling the animation to stop
        this.animateImagesInterval = setInterval(() => {
          this.shiftImages();

          // Stop animation
          if (!this.isAnimating) {
            this.clearAnimateImagesInterval();
            this.setAnimationClass = false;
          }
        }, 3000);
      },

      clearAnimateImagesInterval() {
        if (this.animateImagesInterval) {
          // Clear interval and unshift images to the latest order to prevent
          // a jump to a new image when the animations is finished
          clearInterval(this.animateImagesInterval);
          this.animateImagesInterval = null;
          this.unShiftImages();
        }
      },

      animateImagesStop() {
        if (!this.isAnimating) {
          return;
        }

        this.isAnimating = false;
      },
    },
  };
</script>
